import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-01 at v1.9104, CB-CONV §12 the venue page: the reads the page composes, each from the
// module that owns it (V-1). A read the user may not make is never asked for: the page leaves its
// stat or problem out rather than showing it empty.

export type VenueJob = components['schemas']['VenueJobView'];
export type VenueQuote = components['schemas']['VenueQuoteView'];
export type CrewInduction = components['schemas']['CrewInductionView'];
export type InvoiceSummary = components['schemas']['InvoiceSummaryView'];

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

/** The live jobs at a venue overlapping [from, to); rooms short only for 14 days or less. */
export const venueJobsQuery = (venueId: string, from: string, to: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'jobs', from, to],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{venueId}/jobs', {
        params: { path: { venueId }, query: { from, to } },
      });
      if (error) throw problem(error, 'Could not load the venue’s jobs.');
      return data;
    },
  });

/** Open and accepted quotes at a venue; their totals only with prices.view. */
export const venueQuotesQuery = (venueId: string, from: string, to: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'quotes', from, to],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{venueId}/quotes', {
        params: { path: { venueId }, query: { from, to } },
      });
      if (error) throw problem(error, 'Could not load the venue’s quotes.');
      return data;
    },
  });

/** Each person's latest induction for the venue lapsing within 30 days, or lapsed. */
export const lapsingInductionsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'inductions', 'lapsing'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/inductions', {
        params: { path: { id: venueId }, query: { lapsingWithin: '30' } },
      });
      if (error) throw problem(error, 'Could not load the inductions.');
      return data;
    },
  });

/** The venue's issued invoices, unsettled and past due. */
export const overdueInvoicesQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['invoices', { venueId, overdue: true }],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/invoices', {
        params: { query: { venueId, overdue: 'true' } },
      });
      if (error) throw problem(error, 'Could not load the overdue invoices.');
      return data;
    },
  });

/** Every venue's lapsing inductions at once, for the venues list's Needs attention. */
export const allLapsingInductionsQuery = queryOptions({
  queryKey: ['inductions', 'lapsing'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/inductions/lapsing', {
      params: { query: { within: '30' } },
    });
    if (error) throw problem(error, 'Could not load the inductions.');
    return data;
  },
});

/** Every overdue invoice, each naming its venue, for the venues list's Needs attention. */
export const allOverdueInvoicesQuery = queryOptions({
  queryKey: ['invoices', { overdue: true }],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/invoices', {
      params: { query: { overdue: 'true' } },
    });
    if (error) throw problem(error, 'Could not load the overdue invoices.');
    return data;
  },
});

/** Each person's latest induction for the venue lapsing within a year, or lapsed: the Rules tab. */
export const venueInductionsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'inductions', 'year'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/inductions', {
        params: { path: { id: venueId }, query: { lapsingWithin: '365' } },
      });
      if (error) throw problem(error, 'Could not load the inductions.');
      return data;
    },
  });
